<template>
  <div class="wg-order-history">
    <div
      v-if="ordersLoading"
      class="wg-order-history__skeleton"
      aria-busy="true"
      aria-label="Loading orders"
    >
      <div class="wg-order-history__skeleton-toolbar">
        <div class="wg-order-history__skeleton-bar wg-order-history__skeleton-bar--title" />
        <div class="wg-order-history__skeleton-bar wg-order-history__skeleton-bar--sort" />
      </div>
      <div class="wg-order-history__skeleton-table">
        <div class="wg-order-history__skeleton-thead">
          <div
            v-for="col in skeletonColCount"
            :key="'h-' + col"
            class="wg-order-history__skeleton-cell"
          />
        </div>
        <div v-for="row in 6" :key="'r-' + row" class="wg-order-history__skeleton-trow">
          <div
            v-for="col in skeletonColCount"
            :key="row + '-' + col"
            class="wg-order-history__skeleton-cell"
          />
        </div>
      </div>
    </div>

    <template v-else>
      <EmptyUI v-if="sortedRows.length === 0" />

      <template v-else>
        <div class="wg-order-history__toolbar">
          <h2 class="wg-order-history__title">All your orders for this site</h2>
          <label class="wg-order-history__sort">
            <span class="wg-order-history__sort-label">Sorted by:</span>
            <select v-model="sortOrder" class="wg-order-history__select">
              <option value="recent">Most recent</option>
              <option value="oldest">Oldest first</option>
            </select>
          </label>
        </div>

        <div class="wg-order-history__table-wrap">
          <table class="wg-order-history__table">
            <thead>
              <tr>
                <th>Order</th>
                <th class="text-center">Order type</th>
                <th class="text-center">Keywords amount</th>
                <th class="text-center">Date</th>
                <th class="text-center">Amount paid</th>
                <th class="text-center">Status</th>
                <th v-if="showCommissionColumn" class="text-center">Commission</th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in sortedRows" :key="`${row.type}-${row.id}`">
                <td>
                  <router-link
                    v-if="getOrderPath(row)"
                    class="wg-order-history__order-link font-weight-bold"
                    :to="getOrderPath(row)"
                  >
                    Order #{{ row.id }}
                  </router-link>
                  <span v-else class="wg-order-history__order-text">Order #{{ row.id }}</span>
                </td>
                <td class="text-center">
                  <span
                    class="wg-order-history__badge"
                    :class="orderTypeBadgeClass(row.type)"
                  >
                    {{ orderTypeBadgeLabel(row.type) }}
                  </span>
                </td>
                <td class="text-center">
                  {{ keywordAmountLabel(row) }}
                  <!-- POP Authority booster: due / delivered sub-line (design OrdersTab) -->
                  <div
                    v-if="row.type === 'booster_order'"
                    class="wg-order-history__subline"
                    :class="{ 'wg-order-history__subline--green': boosterIsDelivered(row) }"
                  >{{ boosterDueLabel(row) }}</div>
                </td>
                <td class="text-center">{{ formatOrderDate(row) }}</td>
                <td class="text-center" style="font-weight: 700;">{{ rowPriceLabel(row) }}</td>
                <td class="text-center wg-order-history__status-cell">
                  <b-dropdown
                    v-if="showAdminOrderControls && canEditRowFulfillment(row)"
                    aria-role="list"
                    class="wg-order-history__mc-dropdown wg-order-history__mc-dropdown--status"
                    style="text-align: left;"
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="wg-order-history__mc-trigger"
                        :class="statusDropdownTriggerClass(row)"
                      >
                        <span>{{ fulfillementStatusLabel(rowFulfillementStatus(row)) }}</span>
                        <i class="material-icons-outlined wg-order-history__mc-trigger__caret">expand_more</i>
                      </button>
                    </template>
                    <b-dropdown-item
                      v-if="rowFulfillementStatus(row) !== 'pending'"
                      aria-role="listitem"
                      @click="patchAdminOrder(row, { set_pending: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('pending') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="rowFulfillementStatus(row) !== 'in_progress'"
                      aria-role="listitem"
                      @click="patchAdminOrder(row, { set_in_progress: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('in_progress') }}
                    </b-dropdown-item>
                    <b-dropdown-item
                      v-if="rowFulfillementStatus(row) !== 'completed'"
                      aria-role="listitem"
                      @click="patchAdminOrder(row, { set_completed: true })"
                    >
                      Mark as {{ getFulfillementStatusLabel('completed') }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <span
                    v-else-if="row.type === 'booster_order'"
                    class="wg-order-history__status"
                    :class="boosterStatusClass(row)"
                  >
                    {{ boosterChip(row).label }}
                  </span>
                  <span
                    v-else-if="row.type === 'package_invoice'"
                    class="wg-order-history__status"
                    :class="row.invoice_status === 'paid' ? 'wg-order-history__status--ready' : 'wg-order-history__status--ordered'"
                  >
                    {{ row.invoice_status === 'paid' ? 'Paid' : 'Awaiting payment' }}
                  </span>
                  <span
                    v-else
                    class="wg-order-history__status"
                    :class="statusBadgeClass(rowFulfillementStatus(row))"
                  >
                    {{ fulfillementStatusLabel(rowFulfillementStatus(row)) }}
                  </span>
                </td>
                <td v-if="showCommissionColumn" class="text-center wg-order-history__commission-cell">
                  <b-dropdown
                    v-if="!orderIsIncludedInPackage(row)"
                    aria-role="list"
                    class="wg-order-history__mc-dropdown wg-order-history__mc-dropdown--sales"
                    style="text-align: left;"
                  >
                    <template #trigger>
                      <button
                        type="button"
                        class="wg-order-history__mc-trigger"
                        :class="rowSalesTagTriggerClass(row)"
                      >
                        <span>{{ rowSalesTagLabel(row) }}</span>
                        <i class="material-icons-outlined wg-order-history__mc-trigger__caret">expand_more</i>
                      </button>
                    </template>
                    <b-dropdown-item
                      :disabled="row.sales_tag === 'passive'"
                      aria-role="listitem"
                      @click="patchAdminOrder(row, { sales_tag: 'passive' })"
                    >
                      Set as passive
                    </b-dropdown-item>
                    <b-dropdown-item
                      :disabled="row.sales_tag === 'active'"
                      aria-role="listitem"
                      @click="patchAdminOrder(row, { sales_tag: 'active' })"
                    >
                      Set as <b>active</b>
                    </b-dropdown-item>
                  </b-dropdown>
                  <span v-else class="wg-order-history__action-muted">No commission</span>
                </td>
                <td class="text-right">
                  <!-- POP Authority booster orders: billing actions live here (design OrdersTab) -->
                  <template v-if="row.type === 'booster_order'">
                    <div class="wg-order-history__actions">
                      <a
                        v-if="boosterStatusKey(row) === BOOSTER_STATUS.INVOICE_SENT && row.stripe_hosted_invoice_url"
                        class="wg-order-history__action-link"
                        :href="row.stripe_hosted_invoice_url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Pay invoice
                      </a>
                      <a
                        v-if="boosterStatusKey(row) === BOOSTER_STATUS.DELIVERED && row.report_url"
                        class="wg-order-history__action-link"
                        :href="row.report_url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View report
                      </a>
                      <a
                        v-if="boosterStatusKey(row) !== BOOSTER_STATUS.INVOICE_SENT && row.stripe_hosted_invoice_url"
                        class="wg-order-history__action-link"
                        :href="row.stripe_hosted_invoice_url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View invoice
                      </a>
                      <router-link
                        v-if="boosterShowViewOrder(row)"
                        class="wg-order-history__action-link"
                        :to="boosterDeliverablesPath(row)"
                      >
                        View order
                      </router-link>
                      <template v-if="showAdminOrderControls">
                        <button
                          v-if="boosterStatusKey(row) === BOOSTER_STATUS.INVOICE_SENT"
                          type="button"
                          class="wg-order-history__admin-link"
                          @click="openBoosterConfirm(row, 'mark_paid')"
                        >
                          Mark paid (admin)
                        </button>
                        <button
                          v-if="boosterStatusKey(row) === BOOSTER_STATUS.INVOICE_SENT"
                          type="button"
                          class="wg-order-history__admin-link"
                          @click="openBoosterConfirm(row, 'cancel_invoice')"
                        >
                          Cancel invoice (admin)
                        </button>
                        <button
                          v-if="boosterStatusKey(row) === BOOSTER_STATUS.ORDERED"
                          type="button"
                          class="wg-order-history__admin-link"
                          @click="openBoosterConfirm(row, 'cancel_refund')"
                        >
                          Cancel &amp; refund (admin)
                        </button>
                        <router-link
                          v-if="[BOOSTER_STATUS.ORDERED, BOOSTER_STATUS.IN_DELIVERY].includes(boosterStatusKey(row))"
                          class="wg-order-history__admin-link"
                          :to="boosterDeliverablesPath(row)"
                        >
                          Deliverables (admin)
                        </router-link>
                      </template>
                      <span v-if="!boosterHasAction(row)" class="wg-order-history__action-muted">—</span>
                    </div>
                  </template>
                  <template v-else>
                    <a
                      v-if="row.type === 'package_invoice' && row.invoice_status !== 'paid' && row.stripe_hosted_invoice_url"
                      class="wg-order-history__action-link"
                      :href="row.stripe_hosted_invoice_url"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Pay invoice
                    </a>
                    <router-link
                      v-else-if="getOrderPath(row)"
                      class="wg-order-history__action-link"
                      :to="getOrderPath(row)"
                    >
                      {{ row.type === 'package_invoice' ? 'View package' : 'View order' }}
                    </router-link>
                    <span v-else class="wg-order-history__action-muted">—</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>

    <booster-confirm-modal
      v-if="boosterConfirm"
      v-bind="boosterConfirmProps"
      :busy="boosterBusy"
      @close="boosterConfirm = null"
      @confirm="submitBoosterConfirm"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { formatCurrency, getPureServicePkgName } from '@/utils'
import { whiteGlovePlans } from '@/components/plans_constants'
import {
  POP_AUTHORITY_BOOSTER_STATUS,
  POP_AUTHORITY_BOOSTERS_ROUTE,
  popAuthorityBoosterChip,
  popAuthorityBoosterDetails,
  popAuthorityBoosterDueLabel,
  popAuthorityBoosterIsCanceled,
  popAuthorityBoosterIsDelivered,
  popAuthorityBoosterStatusKey,
  popAuthorityIsNextAudit,
  popAuthorityOrderIsIncluded,
  popAuthorityPackageMonthLabel,
  showPopAuthorityToast,
} from '@/utils/popAuthority'
import { getFulfillementStatusLabel } from '@/components/Campaigns/content-orders/utils.js'
import BoosterConfirmModal from '@/components/SitesSingleView/PopAuthority/BoosterConfirmModal.vue'
import EmptyUI from './EmptyUI.vue'

export default {
  name: 'OrderHistoryWhiteGlove',
  components: { BoosterConfirmModal, EmptyUI },
  inject: {
    // both single-site layouts provide it; booster admin actions refresh the site statuses
    refreshWhiteGloveOrders: { default: null },
  },
  data () {
    return {
      BOOSTER_STATUS: POP_AUTHORITY_BOOSTER_STATUS,
      ordersLoading: true,
      siteOrders: [],
      sortOrder: 'recent',
      /** { kind: 'mark_paid' | 'cancel_invoice' | 'cancel_refund', row } */
      boosterConfirm: null,
      boosterBusy: false,
    }
  },
  computed: {
    siteId () {
      return Number(this.$route.params.siteId)
    },
    siteWhiteGloveOrders () {
      return this.siteOrders.filter((o) => o.site_id === this.siteId)
    },
    sortedRows () {
      const rows = [...this.siteWhiteGloveOrders]
      const mult = this.sortOrder === 'recent' ? -1 : 1
      rows.sort((a, b) => {
        const ta = a.order_date_timestamp || 0
        const tb = b.order_date_timestamp || 0
        if (ta === tb) return 0
        return ta < tb ? -1 * mult : 1 * mult
      })
      return rows
    },
    user () {
      return this.$store.state.user
    },
    isWhiteGloveUser () {
      if (!this.user) return false
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : this.user.accountType
      return whiteGlovePlans.includes(getPureServicePkgName(accountType))
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isWhiteGloveAdminMode () {
      return this.$store.state.isWhiteGloveAdminMode
    },
    showAdminOrderControls () {
      return this.isWhiteGloveUser && this.isMissionControlSession && this.isWhiteGloveAdminMode
    },
    /** POP Authority commissions are fixed amounts: no active / passive sale to pick per order. */
    showCommissionColumn () {
      return this.showAdminOrderControls && !this.$store.getters.isPopAuthorityUser
    },
    skeletonColCount () {
      return this.showCommissionColumn ? 8 : 7
    },
    /** Copy of the admin confirmation for the pending booster action (POP Authority). */
    boosterConfirmProps () {
      const c = this.boosterConfirm
      if (!c) return {}
      const details = popAuthorityBoosterDetails(c.row)
      const price = formatCurrency(c.row.price_on_checkout)
      if (c.kind === 'mark_paid') {
        return {
          title: 'Mark invoice paid (admin)',
          body: `Marks the invoice for ${details} as paid outside Stripe. The booster moves to Ordered, its 7-day `
            + 'delivery window starts today and the campaign manager gets the Asana task.',
          confirmLabel: 'Mark paid',
          confirmTone: 'admin',
        }
      }
      if (c.kind === 'cancel_invoice') {
        return {
          title: 'Cancel invoice (admin)',
          body: `Voids the Stripe invoice for ${details}. The client is not charged, is emailed, and the row `
            + 'shows as Canceled.',
          reasonLabel: 'Reason (optional, kept on the order)',
          confirmLabel: 'Cancel invoice',
          confirmTone: 'admin',
        }
      }
      return {
        title: 'Cancel & refund (admin)',
        body: `Refunds ${price} to the client's card and cancels the ${details} delivery. The Asana task is `
          + 'closed and the client is emailed.',
        reasonLabel: 'Reason (optional, kept on the order)',
        confirmLabel: 'Refund and cancel',
        confirmTone: 'admin',
      }
    },
  },
  watch: {
    '$store.state.triggerRockyRefresh': function () {
      this.handleRockyRefresh()
    },
  },
  mounted () {
    this.loadSiteOrders()
  },
  methods: {
    getFulfillementStatusLabel,
    formatCurrency,
    handleRockyRefresh () {
      this.loadSiteOrders({ silent: true })
    },
    loadSiteOrders (opts = {}) {
      const silent = opts.silent === true
      if (!silent) this.ordersLoading = true
      this.$store
        .dispatch('getWhiteGloveOrders')
        .then((response) => {
          this.siteOrders = response.data.data || []
        })
        .catch(() => {
          if (!silent) this.siteOrders = []
        })
        .finally(() => {
          if (!silent) this.ordersLoading = false
        })
    },
    rowFulfillementStatus (row) {
      return row.fulfillement_status || row.fulfillementStatus || ''
    },
    canEditRowFulfillment (row) {
      // package months are paid invoices, not fulfilled orders; booster delivery is driven from
      // the Deliverables tab (its own endpoints), never through the silo order PATCH
      return row.order_status === 'paid' && !['package_invoice', 'booster_order'].includes(row.type)
    },
    statusDropdownTriggerClass (row) {
      const fs = this.rowFulfillementStatus(row)
      if (fs === 'completed') return 'wg-order-history__mc-trigger--status-done'
      if (fs === 'in_progress') return 'wg-order-history__mc-trigger--status-progress'
      if (fs === 'pending') return 'wg-order-history__mc-trigger--status-pending'
      if (fs) return 'wg-order-history__mc-trigger--status-other'
      return 'wg-order-history__mc-trigger--status-pending'
    },
    rowSalesTagLabel (row) {
      return row.sales_tag === 'active' ? 'Active' : 'Passive'
    },
    rowSalesTagTriggerClass (row) {
      return row.sales_tag === 'active'
        ? 'wg-order-history__mc-trigger--sales-active'
        : 'wg-order-history__mc-trigger--sales-passive'
    },
    patchAdminOrder (row, body) {
      this.$store.commit('showLoading')
      let promise
      if (row.type === 'audit_order') {
        promise = this.$store.dispatch('updateSiteContentAudit', { auditId: row.id, body })
      } else if (row.type === 'package_invoice') {
        promise = this.$store.dispatch('updatePopAuthorityPackageInvoice', {
          invoiceId: row.id,
          body: { ...body, update_commission: true },
        })
      } else if (row.type === 'booster_order') {
        promise = this.$store.dispatch('updatePopAuthorityBoosterOrder', {
          orderId: row.id,
          body: { ...body, update_commission: true },
        })
      } else {
        promise = this.$store.dispatch('updateSiloContentReportOrder', { orderId: row.id, body })
      }

      promise
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.loadSiteOrders({ silent: true })
          }
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    // --- POP Authority booster rows (Phase 8) ---
    boosterStatusKey: popAuthorityBoosterStatusKey,
    boosterChip: popAuthorityBoosterChip,
    boosterDueLabel: popAuthorityBoosterDueLabel,
    boosterIsDelivered: popAuthorityBoosterIsDelivered,
    boosterStatusClass (row) {
      const tone = popAuthorityBoosterChip(row).tone
      return {
        'wg-order-history__status--ordered': tone === 'orange',
        'wg-order-history__status--booster-blue': tone === 'blue',
        'wg-order-history__status--ready': tone === 'green',
        'wg-order-history__status--booster-grey': tone === 'grey',
      }
    },
    boosterDeliverablesPath (row) {
      return { name: 'AllContentWhiteGlove', params: { siteId: String(row.site_id) }, query: { tab: 'boosters' } }
    },
    boosterShowViewOrder (row) {
      if (popAuthorityBoosterIsCanceled(row) || row.stripe_hosted_invoice_url) return false
      return popAuthorityBoosterStatusKey(row) !== POP_AUTHORITY_BOOSTER_STATUS.DELIVERED || !row.report_url
    },
    boosterHasAction (row) {
      return Boolean(row.stripe_hosted_invoice_url || row.report_url || !popAuthorityBoosterIsCanceled(row))
    },
    openBoosterConfirm (row, kind) {
      this.boosterConfirm = { row, kind }
    },
    submitBoosterConfirm ({ reason }) {
      const c = this.boosterConfirm
      if (!c) return Promise.resolve()
      if (c.kind === 'mark_paid') {
        return this.runBoosterAdminAction(
          'markPopAuthorityBoosterOrderPaid',
          { orderId: c.row.id },
          'Marked paid: the booster is Ordered and the campaign manager has the Asana task'
        )
      }
      return this.runBoosterAdminAction(
        'cancelPopAuthorityBoosterOrder',
        { orderId: c.row.id, body: { reason: reason || null, refundMode: 'stripe' } },
        c.kind === 'cancel_invoice'
          ? 'Invoice canceled: the client was emailed'
          : 'Refunded and canceled: the client was emailed'
      )
    },
    runBoosterAdminAction (action, payload, successText) {
      if (this.boosterBusy) return Promise.resolve()
      this.boosterBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch(action, payload)
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status === 'SUCCESS') {
            this.boosterConfirm = null
            // design: confirmations use the teal toast (PaToast in the POP Authority layout); errors stay
            // red notifications. Booster rows only exist on POP Authority projects, so the host is there.
            showPopAuthorityToast(this, successText)
            this.loadSiteOrders({ silent: true })
            if (typeof this.refreshWhiteGloveOrders === 'function') this.refreshWhiteGloveOrders()
            return
          }
          this.$notify({ group: 'info', type: 'error', text: data.msg || 'That did not work, please try again.' })
        })
        .catch((e) => {
          const data = (e && e.response && e.response.data) || {}
          this.$notify({ group: 'info', type: 'error', text: data.msg || 'That did not work, please try again.' })
          this.loadSiteOrders({ silent: true })
        })
        .finally(() => {
          this.boosterBusy = false
          this.$store.commit('hideLoading')
        })
    },
    getOrderPath (order) {
      if (order.type === 'silo_order') {
        const siteId = order.silo?.siteId ?? order.site_id
        const siloId = order.siloId ?? order.silo?.id
        if (siteId != null && siloId != null) {
          return {
            name: 'ContentPlanDetailWhiteGlove',
            params: {
              siteId: String(siteId),
              siloId: String(siloId),
            },
          }
        }
      }
      if (order.type === 'quick_content_order') {
        const siteId = order.silo?.siteId ?? order.site_id
        const siloId = order.siloId ?? order.silo?.id
        if (siteId != null && siloId != null) {
          return {
            name: 'ContentOnlyDetailWhiteGlove',
            params: {
              siteId: String(siteId),
              siloId: String(siloId),
            },
          }
        }
      }
      if (order.type === 'audit_order') {
        return `/sites/${order.site_id}/tools`
      }
      if (order.type === 'package_invoice') {
        return { name: 'PopAuthorityPackages', params: { siteId: String(order.site_id) } }
      }
      if (order.type === 'booster_order') {
        return { name: POP_AUTHORITY_BOOSTERS_ROUTE, params: { siteId: String(order.site_id) } }
      }
      return null
    },
    orderTypeBadgeLabel (type) {
      if (type === 'audit_order') return 'Audit'
      if (type === 'package_invoice') return 'Package'
      if (type === 'booster_order') return 'Booster'
      return 'Content'
    },
    orderTypeBadgeClass (type) {
      if (type === 'audit_order') return 'wg-order-history__badge--audit'
      if (type === 'package_invoice') return 'wg-order-history__badge--package'
      if (type === 'booster_order') return 'wg-order-history__badge--booster'
      return 'wg-order-history__badge--content'
    },
    keywordAmountLabel (row) {
      if (row.type === 'booster_order') {
        return popAuthorityBoosterDetails(row)
      }
      if (row.type === 'package_invoice') {
        const name = row.package_name || 'Package'
        return row.cycle_number ? `${name}, month ${row.cycle_number}` : name
      }
      if (row.type === 'audit_order') {
        return popAuthorityIsNextAudit(row) ? 'Next content audit' : '—'
      }
      const top = (row.selected_top_level_keyword_ids || []).length
      const sup = (row.selected_supporting_keyword_ids || []).length
      const n = top + sup
      if (n === 0) return '—'
      const label = `${n} ${n === 1 ? 'keyword' : 'keywords'}`
      // POP Authority: the order was produced by a package month
      if (popAuthorityOrderIsIncluded(row)) return `${label} · ${popAuthorityPackageMonthLabel(row)}`
      return label
    },
    /** Package months are covered by the monthly subscription, not charged per order. */
    rowPriceLabel (row) {
      if (this.orderIsIncludedInPackage(row)) return 'Included'
      return formatCurrency(row.price_on_checkout)
    },
    /** Content of a package month, or the free next content audit: nothing charged, no commission. */
    orderIsIncludedInPackage (row) {
      return popAuthorityOrderIsIncluded(row) || popAuthorityIsNextAudit(row)
    },
    formatOrderDate (row) {
      const ts = row.order_date_timestamp
      if (ts) {
        return moment.unix(ts).format('MMM. D')
      }
      if (row.order_date) {
        const m = moment(row.order_date)
        return m.isValid() ? m.format('MMM. D') : row.order_date
      }
      return '—'
    },
    fulfillementStatusLabel (status) {
      if (status === 'pending') return 'Ordered'
      if (status === 'in_progress') return 'In progress'
      if (status === 'completed') return 'Ready'
      if (!status) return '—'
      return String(status).replace(/_/g, ' ')
    },
    statusBadgeClass (status) {
      const s = status || ''
      return {
        'wg-order-history__status--ordered': s === 'pending',
        'wg-order-history__status--progress': s === 'in_progress',
        'wg-order-history__status--ready': s === 'completed',
        'wg-order-history__status--other': s && !['pending', 'in_progress', 'completed'].includes(s),
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@keyframes wg-order-history-skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }

  100% {
    background-position: -100% 0;
  }
}

.wg-order-history__skeleton {
  padding: 4px 0 8px;
}

.wg-order-history__skeleton-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.wg-order-history__skeleton-bar {
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-order-history-skeleton-shimmer 1.35s ease-in-out infinite;
}

.wg-order-history__skeleton-bar--title {
  height: 22px;
  width: min(320px, 55%);
}

.wg-order-history__skeleton-bar--sort {
  height: 36px;
  width: 200px;
  max-width: 100%;
}

.wg-order-history__skeleton-table {
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
  background: #fff;
}

.wg-order-history__skeleton-thead {
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  background: #f0f1f5;
  border-bottom: 1px solid var(--v-gray12Color-base);
}

.wg-order-history__skeleton-trow {
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid #ececf0;

  /* Skip thead (first child); match tbody zebra: 2nd, 4th, … data rows */
  &:nth-child(2n + 3) {
    background: #fafafc;
  }

  &:last-child {
    border-bottom: none;
  }
}

.wg-order-history__skeleton-cell {
  flex: 1;
  min-width: 0;
  height: 14px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #e8e9ed 0%,
    #f2f3f6 45%,
    #e8e9ed 90%
  );
  background-size: 200% 100%;
  animation: wg-order-history-skeleton-shimmer 1.35s ease-in-out infinite;

  &:nth-child(1) {
    flex: 0.9;
  }

  &:nth-child(2) {
    flex: 0.85;
  }

  &:nth-child(3) {
    flex: 0.7;
  }

  &:nth-child(4) {
    flex: 0.75;
  }

  &:nth-child(5) {
    flex: 0.65;
  }

  &:nth-child(6) {
    flex: 0.7;
  }

  &:nth-child(7) {
    flex: 0.55;
  }

  &:nth-child(8) {
    flex: 0.55;
  }
}

.wg-order-history__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.wg-order-history__title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--v-mainColor-base);
}

.wg-order-history__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: var(--v-mainColor-base);
}

.wg-order-history__sort-label {
  font-weight: 500;
}

.wg-order-history__select {
  padding: 6px 28px 6px 10px;
  background: #fff;
  font-size: 0.9rem;
  color: var(--v-mainColor-base);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23555' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
}

.wg-order-history__table-wrap {
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
  background: #fff;
}

.wg-order-history__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  thead th {
    text-align: left;
    padding: 14px 16px;
    background: #f0f1f5;
    font-weight: 600;
    color: var(--v-mainColor-base);
    border-bottom: 1px solid var(--v-gray12Color-base);
  }

  tbody td {
    padding: 14px 16px;
    color: var(--v-mainColor-base);
    border-bottom: 1px solid #ececf0;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:nth-child(even) {
    background: #fafafc;
  }
}

.wg-order-history__order-link {
  font-weight: 600;
  color: #0d6b6b;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-order-history__order-text {
  font-weight: 600;
  color: var(--v-mainColor-base);
}

.wg-order-history__badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #F7F5FB;
  color: #6A6A6A;
}

.wg-order-history__badge--package {
  background: #f1e6f6;
  color: #5b2d7a;
}

/* POP Authority booster orders (Phase 8) */
.wg-order-history__badge--booster {
  background: var(--v-lightYellow2Color-base, #fff4ee);
  color: var(--v-profileAnchorColor-base);
}

.wg-order-history__subline {
  margin-top: 3px;
  font-size: 0.75rem;
  color: var(--v-gray2Color-base, #757575);
}

.wg-order-history__subline--green {
  color: #11A33D;
}

.wg-order-history__status--booster-blue {
  background: #316BFF1A;
  color: #316BFF;
}

.wg-order-history__status--booster-grey {
  background: #F0F1F5;
  color: #6A6A6A;
}

.wg-order-history__actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.wg-order-history__admin-link {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  color: #7a3e8f;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-order-history__status {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.wg-order-history__status--ordered {
  background: #FF57001A;
  color: #FF5700;
}

.wg-order-history__status--progress {
  background: #EF34A41A;
  color: #EF34A4;
}

.wg-order-history__status--ready {
  background: #34A8531A;
  color: #11A33D;
}

.wg-order-history__status--other {
  background: #8439FF1A;
  color: #8439FF;
}

.wg-order-history__action-link {
  font-weight: 600;
  color: var(--v-profileAnchorColor-base);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.wg-order-history__action-muted {
  color: var(--v-gray60Color-base, #999);
}

.wg-order-history__status-cell,
.wg-order-history__commission-cell {
  vertical-align: middle;
}

::v-deep .wg-order-history__mc-dropdown .dropdown-trigger {
  display: inline-flex;
}

::v-deep .wg-order-history__mc-dropdown .dropdown-menu {
  min-width: 200px;
}

.wg-order-history__mc-trigger {
  min-width: 108px;
  height: 28px;
  padding: 0 10px 0 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  line-height: 1.2;
}

.wg-order-history__mc-dropdown--status .wg-order-history__mc-trigger {
  min-width: 128px;
}

.wg-order-history__mc-trigger__caret {
  flex-shrink: 0;
  font-size: 1.125rem !important;
  color: rgba(0, 0, 0, 0.45);
}

.wg-order-history__mc-trigger--status-progress {
  color: #ef34a4;
}

.wg-order-history__mc-trigger--status-done {
  color: #11a33d;
}

.wg-order-history__mc-trigger--status-pending {
  color: #ff5700;
}

.wg-order-history__mc-trigger--status-other {
  color: #8439ff;
}

.wg-order-history__mc-trigger--sales-active {
  color: var(--v-mainColor-base);
}

.wg-order-history__mc-trigger--sales-passive {
  color: var(--v-mainColor-base);
}
</style>
